import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { LocationListLocalStoreService } from '../location-list/location-list-local-store.service';
import { delayWhen, distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';
import { animationFrameScheduler, auditTime, combineLatestWith, Observable, of, pairwise, timer } from 'rxjs';
import { CCRowSetting } from '../../../models/locations-models/cc-row-setting';
import { CcListApiService } from './cc-list-api.service';
import { ColumnState, IRowNode } from 'ag-grid-community';
import { CCLocation } from '../../../models/locations-models/cc-location';
import { Store } from '@ngrx/store';
import { selectCcLocationsLoading, selectHoveredCcRowSetting, selectSelectedCcRows } from './state/cc-list-selectors';
import { getFromLocalStorage, setToLocalStorage } from '../../../core/utilities';
import { LocalStorageKeys } from '../enums/local-storage-keys';
import { concatLatestFrom } from '@ngrx/operators';
import { Dictionary } from '@ngrx/entity';
import { CC_COLUMN_DEFS } from './cc-list-constants';
import { CcFilterModel, FilterModel } from '../../../models/locations-models/filter-map';
import { addOrRemoveRowClass } from '../location-list/location-list-utils';


export interface CcListLocalState {
  gridReady: boolean;
  columnState: ColumnState[],
  filterModel: CcFilterModel
}

const initColumnState = () => {
  const persistedColumnState = getFromLocalStorage(LocalStorageKeys.CC_COLUMN_STATE_KEY);
  return persistedColumnState ?
      persistedColumnState :
      CC_COLUMN_DEFS.map(colDef => {
    const columnState: ColumnState = { colId: colDef.colId };
    return columnState;
  });
}

@Injectable()
export class CcListLocalStoreService extends ComponentStore<CcListLocalState> {
  // give the grid some extra time to render rows, otherwise there are CD issues if grid api is used
  // to apply row selection for instance
  public readonly delayedLoading$ = this.store.select(selectCcLocationsLoading)
      .pipe(
          // when switching focusedLocation, loading becomes briefly undefined which
          // would allow to make grid api operations sooner than rows are rendered
          filter(loading => typeof loading !== 'undefined'),
          delayWhen((loading) => loading ?
              of(null).pipe(take(1)) :
              timer(750).pipe(take(1)))
      );
  // use this to time effects that invoke api methods on rows
  private readonly gridReady$ = this.select(this.state$,
      this.delayedLoading$, (state, delayedLoading) => state.gridReady && !delayedLoading);

  public readonly columnState$ = this.select(state => state.columnState);
  public readonly filterModel$ = this.select(state => state.filterModel);

  constructor(private locationListLocalStore: LocationListLocalStoreService,
              private ccListApi: CcListApiService,
              private store: Store) {
    super({
      gridReady: false,
      columnState: initColumnState(),
      filterModel: {}
    });

    // sync store => grid
    this.syncGridSelectionState$(this.store.select(selectSelectedCcRows));
    this.syncVisibleColumnsWithLocationsGrid$(this.locationListLocalStore.columnStateDictionary$);
    // handle edge cases
    this.deselectSelectedRowsOnFilterChange$(this.filterModel$);
    // persist in browser cache
    this.persistColumnSettings$(this.columnState$);
    this.updateRowStylesOnRowHoverChange$(this.store.select(selectHoveredCcRowSetting));
  }

  // UPDATERS

  readonly updateGridReady = this.updater((state, gridReady: boolean): CcListLocalState => ({
    ...state,
    gridReady
  }));

  readonly updateColumnState = this.updater((state, columnState: ColumnState[]): CcListLocalState => ({
    ...state,
    columnState
  }));

  readonly updateFilterModel = this.updater((state, filterModel: FilterModel): CcListLocalState => ({
    ...state,
    filterModel
  }));

  // EFFECTS

  /**
   * Sync store selection state with the grid.
   */
  readonly syncGridSelectionState$ = this.effect((trigger$: Observable<CCRowSetting[]>) => {
    return trigger$.pipe(
        combineLatestWith(this.gridReady$),
        filter(([_, gridReady]) => gridReady),
        map(([selectedCcRows, _]) => selectedCcRows
            ?.map(selectedCcRow => selectedCcRow.placeId)),
        tap((ids: string[]) => {
          const selectedNodes: IRowNode<CCLocation>[] = [];
          const deselectedNodes: IRowNode<CCLocation>[] = [];

          this.ccListApi.gridApi?.forEachNode(n => {
            ids.includes(n.id) ?
                selectedNodes.push(n) : deselectedNodes.push(n)
          });
          this.ccListApi.gridApi?.setNodesSelected({ nodes: selectedNodes, newValue: true });
          this.ccListApi.gridApi?.setNodesSelected({ nodes: deselectedNodes, newValue: false });
        }));
  });

  /**
   * Mirror column visibility setting for certain CC columns from location list grid.
   * Handle edge case where hiding a column with an active sorting / filter will reset them and
   * in case of multi-column sorting, sorting prio will be adjusted.
   */
  readonly syncVisibleColumnsWithLocationsGrid$ = this.effect((locationListColumnState$: Observable<Dictionary<ColumnState>>) => {
    return locationListColumnState$.pipe(
        combineLatestWith(this.gridReady$),
        filter(([_, gridReady]) =>
            gridReady),
        concatLatestFrom(() => this.columnState$),
        tap(([[locationListColumnState, _], columnState]) => {
          const newColumnState: ColumnState[] = columnState?.map((col: ColumnState) => ({ ...col }));

          newColumnState.forEach((column) => {
            // set visibility according to main grid
            if (locationListColumnState[column.colId]) {
              column.hide = locationListColumnState[column.colId].hide;
            }
            // reset sorting
            if (column.hide) {
              if (!!column.sort) {
                column.sort = null;
                column.sortIndex = null;
                // decrease sortIndex of other columns by 1
                newColumnState.forEach((col) => {
                  if (!!col.sortIndex && col.colId !== column.colId) {
                    col.sortIndex -= 1;
                  }
                })
              }
              // reset filter
              this.ccListApi.gridApi.setColumnFilterModel(column.colId, null)
                  .then(() => this.ccListApi.gridApi.onFilterChanged());
            }
          });

          this.ccListApi.gridApi.applyColumnState({state: newColumnState, applyOrder: true});
          // sorting indication is not visible without calling this!
          this.ccListApi.gridApi.refreshHeader();
        }));
  });

  /**
   * Make sure selected rows are deselected if they don't pass filtering.
   */
  readonly deselectSelectedRowsOnFilterChange$ = this.effect((filterModel: Observable<CcFilterModel>) => {
    return filterModel.pipe(
        combineLatestWith(this.gridReady$),
        filter(([_, gridReady]) => gridReady),
        tap(([_, gridReady]) => {
          const toBeSelectedNodes: IRowNode<CCLocation>[] = [];
          const toBeDeselectedNodes: IRowNode<CCLocation>[] = [];

          this.ccListApi.gridApi.forEachNode((rowNode) => {
            if (rowNode.displayed && rowNode.isSelected()) {
              toBeSelectedNodes.push(rowNode);
            } else toBeDeselectedNodes.push(rowNode);
          });

          this.ccListApi.gridApi.setNodesSelected({nodes: toBeSelectedNodes, newValue: true});
          this.ccListApi.gridApi.setNodesSelected({nodes: toBeDeselectedNodes, newValue: false});
        }));
  });


  /**
   * Persist column settings in browser cache.
   */
  readonly persistColumnSettings$ = this.effect((columnState$: Observable<ColumnState[]>) => {
    return columnState$.pipe(
        filter((columnState) => !!columnState.length),
        tap((columnState) => {
          setToLocalStorage(LocalStorageKeys.CC_COLUMN_STATE_KEY, columnState);
        }));
  });

  /**
   * Handle hovered row styling
   */
  readonly updateRowStylesOnRowHoverChange$ = this.effect((hoveredCcRowSetting$: Observable<CCRowSetting>) => {
    return hoveredCcRowSetting$.pipe(
        auditTime(0, animationFrameScheduler), // Batch emissions to the next paint cycle
        distinctUntilChanged(), // Only emit if the ID is different from the last emission
        pairwise(),
        map(([prevSetting, currSetting]) => {
          // 1. Remove class from all segments of the previous row
          if (prevSetting != null) {
            addOrRemoveRowClass(prevSetting?.placeId, 'hoveredRow', 'remove');
          }
          // 2. Add class to all segments of the new row
          if (currSetting != null) {
            addOrRemoveRowClass(currSetting?.placeId, 'hoveredRow', 'add');
          }
        }),
    );
  });

}



